import React from 'react'

import { Field, Formik } from 'formik'

import { ZariContext } from '_zari/context'
import { Modal, Button, Form, Col, Row } from '_zari/core'

import * as EstanteriaService from 'services/estanteria.service'

const TIPOS = [
  { id: 'GENERAL', nombre: 'General' },
  { id: 'SOBRES', nombre: 'Sobres / Documentos' },
  { id: 'CAJAS_PEQ', nombre: 'Cajas pequeñas' },
  { id: 'CAJAS_GRD', nombre: 'Cajas grandes' },
  { id: 'MIXTO', nombre: 'Mixto' }
]

const COLORES = [
  '#1F3A8A',
  '#28A745',
  '#D4AF37',
  '#DC3545',
  '#17A2B8',
  '#6F42C1',
  '#FD7E14'
]

const ModalEstanteria = ({
  show,
  handleClose,
  estanteriaEditar,
  estanteriaPlantilla,
  almacenes,
  onGuardado,
  onClickNuevoAlmacen
}) => {
  const __zariContext = React.useContext(ZariContext)
  const esEdicion = !!(estanteriaEditar && estanteriaEditar._id)
  const fuente = estanteriaEditar || estanteriaPlantilla

  const initialValues = React.useMemo(
    () => ({
      codigo: estanteriaEditar?.codigo || '',
      nombre: estanteriaEditar?.nombre || '',
      descripcion: fuente?.descripcion || '',
      _idAlmacen: fuente?._idAlmacen || '',
      filas: fuente?.filas || 3,
      columnas: fuente?.columnas || 4,
      profundidad: fuente?.profundidad || 1,
      color: fuente?.color || '#1F3A8A',
      tipoContenido: fuente?.tipoContenido || 'GENERAL',
      orden: fuente?.orden || 0
    }),
    [estanteriaEditar, estanteriaPlantilla]
  )

  const submit = React.useCallback(
    async (values, { setSubmitting }) => {
      try {
        const payload = {
          codigo: values.codigo.trim().toUpperCase(),
          nombre: values.nombre.trim(),
          descripcion: values.descripcion?.trim() || '',
          _idAlmacen: values._idAlmacen,
          filas: Math.max(1, Number(values.filas) || 1),
          columnas: Math.max(1, Number(values.columnas) || 1),
          profundidad: Math.max(1, Number(values.profundidad) || 1),
          color: values.color || '#1F3A8A',
          tipoContenido: values.tipoContenido || 'GENERAL',
          orden: Number(values.orden) || 0
        }
        if (!payload.codigo) throw { data: { error: 'El código es obligatorio.' } }
        if (!payload.nombre) throw { data: { error: 'El nombre es obligatorio.' } }
        if (!payload._idAlmacen) throw { data: { error: 'Seleccione un almacén.' } }

        if (esEdicion) {
          await EstanteriaService.actualizar(estanteriaEditar._id, payload)
          __zariContext.snack('success', 'Estantería actualizada')
        } else {
          await EstanteriaService.nuevo(payload)
          __zariContext.snack('success', 'Estantería creada')
        }
        onGuardado()
      } catch (err) {
        __zariContext.snack('error', err?.data?.error || 'Error al guardar')
      } finally {
        setSubmitting(false)
      }
    },
    [__zariContext, esEdicion, estanteriaEditar, onGuardado]
  )

  return (
    <Modal show={show} size='lg'>
      <Modal.Header>
        <Modal.Title>
          {esEdicion
            ? 'Editar Estantería'
            : estanteriaPlantilla
            ? `Duplicar Estantería (basada en ${estanteriaPlantilla.codigo})`
            : 'Nueva Estantería'}
        </Modal.Title>
      </Modal.Header>
      <Formik initialValues={initialValues} onSubmit={submit} enableReinitialize>
        {({ values, isSubmitting, handleSubmit, setFieldValue }) => (
          <Form>
            <Modal.Body>
              <Row>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label text='CÓDIGO *' md={12} />
                    <Col md={12}>
                      <Field name='codigo'>
                        {({ field }) => (
                          <Form.InputText
                            {...field}
                            placeholder='Ej: EST-A'
                            disabled={isSubmitting}
                            id='input-codigo-est'
                          />
                        )}
                      </Field>
                      <small className='text-muted'>Identificador único en la sede</small>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group>
                    <Form.Label text='NOMBRE *' md={12} />
                    <Col md={12}>
                      <Field name='nombre'>
                        {({ field }) => (
                          <Form.InputText
                            {...field}
                            placeholder='Ej: Estantería principal de papelería'
                            disabled={isSubmitting}
                            id='input-nombre-est'
                          />
                        )}
                      </Field>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label text='ALMACÉN *' md={12} />
                    <Col md={12}>
                      <div className='ub-almacen-row'>
                        <Field name='_idAlmacen'>
                          {({ field }) => (
                            <Form.Select {...field} disabled={isSubmitting} id='select-alm-est'>
                              <option value=''>Seleccionar</option>
                              {almacenes.map((a) => (
                                <option key={a._id} value={a._id}>
                                  {a.nombre}
                                </option>
                              ))}
                            </Form.Select>
                          )}
                        </Field>
                        {onClickNuevoAlmacen && (
                          <Button
                            outline
                            color='primary'
                            size='sm'
                            type='button'
                            title='Crear nuevo almacén'
                            onClick={() =>
                              onClickNuevoAlmacen((nuevo) => {
                                if (nuevo && nuevo._id) setFieldValue('_idAlmacen', nuevo._id)
                              })
                            }
                          >
                            <i className='fa fa-plus' />
                          </Button>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label text='TIPO DE CONTENIDO' md={12} />
                    <Col md={12}>
                      <Field name='tipoContenido'>
                        {({ field }) => (
                          <Form.Select {...field} disabled={isSubmitting} id='select-tipo-est'>
                            {TIPOS.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.nombre}
                              </option>
                            ))}
                          </Form.Select>
                        )}
                      </Field>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label text='DESCRIPCIÓN' md={12} />
                    <Col md={12}>
                      <Field name='descripcion'>
                        {({ field }) => (
                          <Form.InputText {...field} disabled={isSubmitting} placeholder='(opcional)' id='input-desc-est' />
                        )}
                      </Field>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>

              <hr />
              <h6 style={{ color: '#1F3A8A' }}>Dimensiones físicas</h6>
              <Row>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label text='FILAS (NIVELES)' md={12} />
                    <Col md={12}>
                      <Field name='filas'>
                        {({ field }) => (
                          <Form.InputNumber
                            {...field}
                            min={1}
                            max={20}
                            disabled={isSubmitting}
                            id='input-filas-est'
                          />
                        )}
                      </Field>
                      <small className='text-muted'>Niveles verticales (ej. 3 niveles de altura)</small>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label text='COLUMNAS' md={12} />
                    <Col md={12}>
                      <Field name='columnas'>
                        {({ field }) => (
                          <Form.InputNumber
                            {...field}
                            min={1}
                            max={30}
                            disabled={isSubmitting}
                            id='input-cols-est'
                          />
                        )}
                      </Field>
                      <small className='text-muted'>Casilleros horizontales por nivel</small>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label text='PROFUNDIDAD' md={12} />
                    <Col md={12}>
                      <Field name='profundidad'>
                        {({ field }) => (
                          <Form.InputNumber
                            {...field}
                            min={1}
                            max={10}
                            disabled={isSubmitting}
                            id='input-prof-est'
                          />
                        )}
                      </Field>
                      <small className='text-muted'>Cuántos items caben en lo profundo</small>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label text='COLOR' md={12} />
                    <Col md={12}>
                      <div className='ub-color-picker'>
                        {COLORES.map((c) => (
                          <button
                            key={c}
                            type='button'
                            className={`ub-color-swatch ${values.color === c ? 'active' : ''}`}
                            style={{ background: c }}
                            onClick={() => setFieldValue('color', c)}
                          />
                        ))}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>

              <div className='alert alert-info mt-3' style={{ fontSize: '0.85rem' }}>
                <i className='fa fa-info-circle mr-1' /> Total de celdas que se generarán:{' '}
                <strong>
                  {(Number(values.filas) || 1) * (Number(values.columnas) || 1) * (Number(values.profundidad) || 1)}
                </strong>
                {esEdicion && ' (las celdas existentes con productos se conservan)'}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button color='secondary' onClick={handleClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting}>
                {esEdicion ? 'Actualizar' : 'Crear'}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

export default ModalEstanteria
